import React from 'react'
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
} from '@mui/material';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { addCommas } from '../../utils/functions/commas';
import { TextareaAutosize } from '@mui/base/TextareaAutosize';
import DeleteIcon from '@mui/icons-material/Delete';
import { IconButton } from '@mui/material';
import { useState } from 'react';
import { swalErrorBtn, swalLoading, swalSuccessBtn } from '../../helpers/swalHelper';
import InvoiceListApi from '../../service/invoice.api';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { locale } from 'dayjs';
import 'dayjs/locale/th';

const ariaLabel = { 'aria-label': 'description' };

function TableUploadSchool({ allowEdit, showData, statusETax }) {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const total = showData?.length || 0;

    const [isSending, setIsSending] = useState(false);

    const DATE_FORMAT = 'DD/MM/YYYY'

    const schema = () => {
        const baseSchema = {
          rows: yup.array().of(
            yup.object().shape({
              invoice_no: yup.string().required('กรุณากรอกเลขที่ใบแจ้งหนี้'),
              so_name: yup.string().required('กรุณากรอกชื่อ'),
              descriptions: yup.string().required('กรุณากรอกรายการ'),
              grade_level: yup.string().required('กรุณากรอกระดับชั้น'),
              year: yup.string().required('กรุณากรอกปีการศึกษา'),
              term: yup.string().required('กรุณากรอกเทอม'),
              inv_start_date: yup
                .date()
                .transform((value, originalValue) => {
                  if (!originalValue) return null
                  const startDate = dayjs(originalValue,DATE_FORMAT, true);
                  return startDate.isValid() ? startDate.toDate() : null
                })
                .typeError('กรุณากรอกวันที่ให้ถูกต้อง (รูปแบบ วว/ดด/ปปปป)')
                .required('กรุณากรอบวันที่ใบแจ้งหนี้')
                .max(yup.ref('inv_due_date'), 'วันที่ใบแจ้งหนี้ต้องไม่เกินวันครบกำหนด'),
              inv_due_date: yup
                .date()
                .transform((value, originalValue) => {
                  if (!originalValue) return null
                  const dueDate = dayjs(originalValue,DATE_FORMAT, true);
                  return dueDate.isValid() ? dueDate.toDate() : null
                })
                .typeError('กรุณากรอกวันที่ให้ถูกต้อง (รูปแบบ วว/ดด/ปปปป)')
                .required('กรุณากรอกวันครบกำหนด')
                .min(yup.ref('inv_start_date'), 'วันครบกำหนดต้องไม่ต่ำกว่าวันที่ใบแจ้งหนี้'),
              amount: yup
                .number()
                .nullable()
                .transform((value, originValue) => (originValue === '' ? null : value) )
                .moreThan(0, 'ยอดชำระต้องมากว่า 0')
                .required('กรุณากรอกยอดชำระ'),
              tel: yup
                .string()
                .required('กรุณากรอกเบอร์โทรศัพท์')
                .matches(/^[0-9]{10}$/, 'กรุณากรอกเลข 10 หลัก'),
              email: yup
                .string()
                .nullable()
                .test(
                  'is-valid-email-list',
                  'กรุณากรอกอีเมลในรูปแบบที่ถูกต้อง (สามารถกรอกหลายอีเมลได้โดยใช้จุลภาคคั่น)',
                  (value) => {
                    // If etaxStatus is true, email is required
                    if (statusETax && !value) {
                      return false; // Email is required when etaxStatus is true
                    }

                    if (value) {
                      const emails = value.split(',').map((email) => email.trim());
                      const emailRegex =
                        /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
                      return emails.every((email) => emailRegex.test(email));
                    }

                    return true; // If no email is provided, it's valid when etaxStatus is false
                  },
                ),


            }),
          ),
        };

        return yup.object().shape(baseSchema);
      };

      const {
        register,
        handleSubmit,
        formState: { errors },
        setValue,
        trigger,
        control,
      } = useForm({
        resolver: yupResolver(schema()),
      });

      const { fields, append, remove } = useFieldArray({
        control,
        name: 'rows',
      });

      const onSubmit = async (item) => {

        if (isSending) return;
        setIsSending(true);

        swalLoading({title: 'กำลังอัปโหลดข้อมูล...'});

        try {
          const updatedData = item.rows.map((row) => {
            return {
              ...row,
              inv_start_date: dayjs(row.inv_start_date).format(DATE_FORMAT),
              inv_due_date: dayjs(row.inv_due_date).format(DATE_FORMAT),
            }
          })

          const data = await InvoiceListApi.uploadInvoiceApi(updatedData);

          if (data.status_message === 'create success') {
            // setOpenDialog(true);
            swalSuccessBtn({
              title: 'อัปโหลดข้อมูลสำเร็จ'
            }).then((result) => {
              window.location.reload();
            });
          }
        } catch (error) {
          console.log(error);
          swalErrorBtn({title: 'อัปโหลดข้อมูลไม่สำเร็จ'});
        } finally {
          setIsSending(false);
        }
      };

      const cancel = () => {
        swalErrorBtn({
          title: 'ยกเลิกการอัปโหลดข้อมูล',
          icon: 'warning'
        }).then((result) => {
          window.location.reload();
        });
      };

      React.useEffect(() => {
        if (showData) {
          // Reset fields before appending new ones
          remove(); // Remove all current rows in the form
          showData.forEach((row) => {
            append({ ...row }); // Append new rows
          });
        }
      }, [showData, append, remove, setValue]);

  return (
    <div>
        <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
            <div style={{ width: '100%', overflowX: 'auto', maxWidth: '90vw', }}>
                <TableContainer component={Paper} style={{ minWidth: 650, maxWidth: '100%', }}>
                    <Table
                      sx={{ minWidth: 650 }}
                      aria-label="simple table"
                      >
                        <TableHead>
                            <TableRow>
                                <TableCell align="center" sx={{ minWidth: 120 }}>เลขที่ใบแจ้งหนี้</TableCell>
                                <TableCell align="center" sx={{ minWidth: 120 }}>ชื่อ</TableCell>
                                <TableCell align="center" sx={{ minWidth: 150 }}>รายการ</TableCell>
                                <TableCell align="center" sx={{ minWidth: 120 }}>ระดับชั้น</TableCell>
                                <TableCell align="center" sx={{ minWidth: 120 }}>ปีการศึกษา</TableCell>
                                <TableCell align="center" sx={{ minWidth: 120 }}>เทอม</TableCell>
                                <TableCell align="center" sx={{ minWidth: 120 }}>วันที่ใบแจ้งหนี้</TableCell>
                                <TableCell align="center" sx={{ minWidth: 120 }}>วันครบกำหนด</TableCell>
                                <TableCell align="center" sx={{ minWidth: 120 }}>ยอดชำระ</TableCell>
                                <TableCell align="center" sx={{ minWidth: 120 }}>เบอร์โทรศัพท์</TableCell>
                                <TableCell align="center" sx={{ minWidth: 120 }}>อีเมลล์</TableCell>
                                <TableCell align="center" sx={{ minWidth: 120 }}>ลบรายการ</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                          {fields.slice(page * rowsPerPage,Math.min(fields.length, (page + 1) * rowsPerPage),).map((row, index) => {
                            const globalIndex = page * rowsPerPage + index;
                            return (
                            <TableRow hover key={row.no || globalIndex}>
                                {/* เลขที่ใบแจ้งหนี้ */}
                                {allowEdit ? (
                                    <TableCell align='center'>
                                        <TextField
                                        variant="standard"
                                        {...register(`rows.${globalIndex}.invoice_no`)}
                                        defaultValue={row.invoice_no}
                                        inputProps={{style: { textAlign: 'center' },}}
                                        error={Boolean(errors?.rows?.[globalIndex]?.invoice_no)}
                                        helperText={errors?.rows?.[globalIndex]?.invoice_no?.message}
                                        />
                                    </TableCell>
                                ) : (
                                    <TableCell align='center'>
                                      {row.invoice_no || '-'}
                                    </TableCell>
                                )}

                                {/* ชื่อลูกค้า */}
                                {allowEdit ? (
                                    <TableCell align="center">
                                      <TextareaAutosize
                                        minRows={1}
                                        defaultValue={row.so_name}
                                        {...register(`rows.${globalIndex}.so_name`)}
                                        aria-label={ariaLabel['aria-label']}
                                        style={{
                                          textAlign: 'left',
                                          width: '120px',
                                          resize: 'none',
                                          border: 'none',
                                          borderBottom: !!errors?.rows?.[globalIndex]?.so_name
                                            ? '1px solid red'
                                            : '1px solid #AFAFAF',
                                          paddingBottom: '4px',
                                          outline: 'none',
                                          background: 'none',
                                        }}
                                      />
                                      {!!errors?.rows?.[globalIndex]?.so_name && (
                                        <p
                                          style={{
                                            color: 'red',
                                            fontSize: '12px',
                                            marginTop: '4px',
                                          }}
                                        >
                                          {errors?.rows?.[globalIndex]?.so_name?.message}
                                        </p>
                                      )}
                                    </TableCell>
                                ) : (
                                    <TableCell align="center">
                                        {row.so_name || '-'}
                                    </TableCell>
                                )}

                                {/* รายละเอียด สินค้า บริการ */}
                                {allowEdit ? (
                                <TableCell align="center">
                                    <TextField
                                        variant="standard"
                                        {...register(`rows.${globalIndex}.descriptions`)}
                                        defaultValue={row.descriptions}
                                        inputProps={{
                                        style: { textAlign: 'center' },
                                        }}
                                        error={!!errors?.rows?.[globalIndex]?.descriptions}
                                        helperText={
                                        errors?.rows?.[globalIndex]?.descriptions?.message
                                        }
                                    />
                                </TableCell>
                                ) : (
                                <TableCell align="center">
                                    {row.descriptions || '-'}
                                </TableCell>
                                )}

                                {/* ระดับชั้น */}
                                {allowEdit ? (
                                <TableCell align="center">
                                    <TextField
                                        variant="standard"
                                        {...register(`rows.${globalIndex}.grade_level`)}
                                        defaultValue={row.grade_level}
                                        inputProps={{
                                        style: { textAlign: 'center' },
                                        }}
                                        error={!!errors?.rows?.[globalIndex]?.grade_level}
                                        helperText={
                                        errors?.rows?.[globalIndex]?.grade_level?.message
                                        }
                                    />
                                </TableCell>
                                ) : (
                                <TableCell align="center">
                                    {row.grade_level || '-'}
                                </TableCell>
                                )}

                                {/* ปีการศึกษา */}
                                {allowEdit ? (
                                <TableCell align="center">
                                    <TextField
                                        variant="standard"
                                        {...register(`rows.${globalIndex}.year`)}
                                        defaultValue={row.year}
                                        inputProps={{
                                        style: { textAlign: 'center' },
                                        }}
                                        error={!!errors?.rows?.[globalIndex]?.year}
                                        helperText={
                                        errors?.rows?.[globalIndex]?.year?.message
                                        }
                                    />
                                </TableCell>
                                ) : (
                                <TableCell align="center">
                                    {row.year || '-'}
                                </TableCell>
                                )}

                                {/* เทอม */}
                                {allowEdit ? (
                                    <TableCell>
                                        <TextField
                                        variant="standard"
                                        {...register(`rows.${globalIndex}.term`)}
                                        defaultValue={row.term}
                                        inputProps={{
                                            type: 'number',
                                            min: 1,
                                            style: { textAlign: 'center' },
                                        }}
                                        error={!!errors?.rows?.[globalIndex]?.term}
                                        helperText={
                                            errors?.rows?.[globalIndex]?.term?.message
                                        }
                                        />
                                    </TableCell>
                                ) : (
                                    <TableCell align="center">
                                        {row.term || '-'}
                                    </TableCell>
                                )}

                                {/* วันที่ใบแจ้งหนี้ */}
                                {allowEdit ? (
                                <TableCell align="center">
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <Controller
                                        name={`rows.${globalIndex}.inv_start_date`}
                                        control={control}
                                        render={({ field }) => (
                                            <DatePicker
                                            value={
                                                field.value ? dayjs(field.value, DATE_FORMAT) : null
                                            }
                                            onChange={(newValue) => {
                                                field.onChange(
                                                newValue
                                                  ? newValue
                                                  : null,
                                                );
                                            }}
                                            inputFormat={DATE_FORMAT}
                                            renderInput={(params) => (
                                                <TextField
                                                {...params}
                                                variant="standard"
                                                error={
                                                    !!errors?.rows?.[globalIndex]
                                                    ?.inv_start_date
                                                }
                                                helperText={
                                                    errors?.rows?.[globalIndex]
                                                    ?.inv_start_date?.message
                                                }
                                                />
                                            )}
                                            />
                                        )}
                                        />
                                    </LocalizationProvider>
                                </TableCell>
                                ) : (
                                <TableCell align="center" >
                                    {row.inv_start_date || '-'}
                                </TableCell>
                                )}

                                {/* วันครบกำหนดชำระเงิน */}
                                {allowEdit ? (
                                <TableCell align="center" >
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <Controller
                                        name={`rows.${globalIndex}.inv_due_date`}
                                        control={control}
                                        render={({ field }) => (
                                            <DatePicker
                                            value={
                                                field.value ? dayjs(field.value, DATE_FORMAT) : null
                                            }
                                            onChange={(newValue) => {
                                                field.onChange(
                                              newValue
                                                  ? newValue
                                                  : null,
                                                );
                                            }}
                                            inputFormat={DATE_FORMAT}
                                            renderInput={(params) => (
                                                <TextField
                                                {...params}
                                                variant="standard"
                                                error={
                                                    !!errors?.rows?.[globalIndex]
                                                    ?.inv_due_date
                                                }
                                                helperText={
                                                    errors?.rows?.[globalIndex]
                                                    ?.inv_due_date?.message
                                                }
                                                />
                                            )}
                                            />
                                        )}
                                        />
                                    </LocalizationProvider>
                                </TableCell>
                                ) : (
                                <TableCell align="center">
                                    {row.inv_due_date || '-'}
                                </TableCell>
                                )}

                                {/* ยอดรวมทั้งหมด (ยอดชำระ) */}
                                {allowEdit ? (
                                    <TableCell>
                                        <TextField
                                        variant="standard"
                                        {...register(`rows.${globalIndex}.amount`)}
                                        defaultValue={row.amount}
                                        inputProps={{
                                            type: 'number',
                                            min: 1,
                                            step: '0.01',
                                            style: { textAlign: 'center' },
                                        }}
                                        error={!!errors?.rows?.[globalIndex]?.amount}
                                        helperText={
                                            errors?.rows?.[globalIndex]?.amount?.message
                                        }
                                        />
                                    </TableCell>
                                ) : (
                                    <TableCell align="center">
                                        {addCommas(row.amount, 2) || '-'}
                                    </TableCell>
                                )}

                                {/*  เบอร์โทรศัพท์ */}
                                {allowEdit ? (
                                    <TableCell>
                                        <TextField
                                        variant="standard"
                                        {...register(`rows.${globalIndex}.tel`)}
                                        defaultValue={row.tel}
                                        inputProps={{
                                            style: { textAlign: 'center' },
                                        }}
                                        error={!!errors?.rows?.[globalIndex]?.tel}
                                        helperText={
                                            errors?.rows?.[globalIndex]?.tel?.message
                                        }
                                        />
                                    </TableCell>
                                ) : (
                                    <TableCell align="center">
                                          {row.tel || '-'}
                                    </TableCell>
                                )}

                                {/* อีเมลล์ */}
                                {allowEdit ? (
                                    <TableCell align="center">
                                        <TextareaAutosize
                                            minRows={1}
                                            defaultValue={row.email}
                                            {...register(`rows.${globalIndex}.email`)}
                                            aria-label={ariaLabel['aria-label']}
                                            style={{
                                            textAlign: 'left',
                                            width: '120px',
                                            resize: 'none',
                                            border: 'none',
                                            borderBottom: !!errors?.rows?.[globalIndex]?.email
                                                ? '1px solid red'
                                                : '1px solid #AFAFAF',
                                            paddingBottom: '4px',
                                            outline: 'none',
                                            background: 'none',
                                            }}
                                        />
                                        {!!errors?.rows?.[globalIndex]?.email && (
                                            <p
                                            style={{
                                                color: 'red',
                                                fontSize: '12px',
                                                marginTop: '4px',
                                            }}
                                            >
                                            {errors?.rows?.[globalIndex]?.email?.message}
                                            </p>
                                        )}
                                    </TableCell>
                                ) : (
                                    <TableCell align="center">
                                    {row.email || '-'}
                                    </TableCell>
                                )}

                                {/* Action */}
                                <TableCell align="center">
                                  <IconButton
                                    onClick={() => remove(globalIndex)}
                                    aria-label="delete"
                                    color="error"
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </TableCell>

                            </TableRow>
                            );
                          })}
                        </TableBody>
                    </Table>
                </TableContainer>
                {/* </Paper> */}

                <TablePagination
                  sx={{
                    width: 'auto',
                    marginRight: '8px',
                    '& .MuiTypography-caption': { display: 'none' },
                  }}
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  component="div"
                  rowsPerPage={rowsPerPage}
                  page={page}
                  count={total}
                  labelRowsPerPage="Rows"
                  onPageChange={(event, newPage) => {
                    setPage(newPage);
                  }}
                  onRowsPerPageChange={(event) => {
                    setRowsPerPage(parseInt(event.target.value, 10));
                    setPage(0);
                  }}
                />
                <div className="mt-7 flex justify-end">
                  <div className="mr-3">
                    {showData && (
                      <Button type="submit" variant="contained">
                        <div className="text-white">ยืนยันอัปโหลดข้อมูล</div>
                      </Button>
                    )}
                  </div>
                  <div>
                    <Button onClick={cancel} variant="outlined" color="error">
                      ยกเลิการอัปโหลด
                    </Button>
                  </div>
                </div>
            </div>
        </form>
    </div>
  )
}

export default TableUploadSchool
